import { CommonDialogs } from "@mcleod/common";
import { AutogenLayoutPnnLaneBuilderProfileList } from "./autogen/AutogenLayoutPnnLaneBuilderProfileList";
import { ClickEvent, Label,  TableSelectionEvent,  DataSourceExecutionEvent, DataSourceAction, SlideoutDecorator, Layout, Button,  TableRowDisplayEvent, Textbox, SelectionType, Panel, SelectionMode } from "@mcleod/components";
import { Api, DateUtil, HorizontalAlignment, ModelRow } from "@mcleod/core";
import { LaneBuilderResultsSlideout } from "./LaneBuilderResultsSlideout";
import { ComponentListenerDefs } from "@mcleod/components/src/base/ComponentListenerDefs";

export class PnnLaneBuilderProfileList extends AutogenLayoutPnnLaneBuilderProfileList {

    /** This is an event handler for the onClick event of buttonRunProfile. */
    async buttonRunProfileOnClick(event: ClickEvent) {
        this.buttonRunProfile.busy = true;
        try {
            await this.sourcePnnLaneBuilder.search({
                profile_id: this.tableLaneBuilder.selectedRow?.data?.get("id"),
            });
        }
        catch (e) {
            CommonDialogs.showError(e);
        }
        finally {
            this.buttonRunProfile.busy = false;
        }
    }

    /** This is an event handler for the onSelect event of tableLaneBuilder. */
    tableLaneBuilderOnSelect(event: TableSelectionEvent) {
        const rowWasSelected = event.newRow?.data != null;
        const isActive = event.newRow?.data.get("is_active") === "Y";
        const enableButton = rowWasSelected && isActive;
        this.buttonRunProfile.enabled = enableButton;
        this.buttonRunProfile.tooltip = enableButton ? "Run Profile" : "Select an active profile to run";
    }

    /** This is an event handler for the afterExecution event of sourcePnnLaneBuilder. */
    async sourcePnnLaneBuilderAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() != DataSourceAction.SEARCH) return;

        const summary = event.dataSource.summaryData;
        const data = event.dataSource.data;

        if (summary?.get("no_delivered_loads")) {
            const dialogLabel = new Label({ text: "No delivered loads found for the selected profile.", align: HorizontalAlignment.CENTER, fillRow: true });
            CommonDialogs.showDialog(dialogLabel);
        }
        else if (summary?.get("no_pnn_lanes")) {
            const dialogLabel = new Label({ text: "No PNN lanes or contacts were created.", align: HorizontalAlignment.CENTER, fillRow: true });
            CommonDialogs.showDialog(dialogLabel);
        }
        else {
            const numCarriers = summary?.get("number_of_carriers");
            const carrierLabel = new Label({ text: numCarriers + " carrier(s) have been updated.", align: HorizontalAlignment.CENTER, fillRow: true });
            const resultLabel = new Label({ text: "Would you like to see the results?", align: HorizontalAlignment.CENTER, fillRow: true, fontBold: true, marginTop: 10 });
            const panel = new Panel({ components: [carrierLabel, resultLabel], fillRow: true, margin: 0 });
            const reviewResults = await CommonDialogs.showYesNo(panel, "Update PNN Carrier");
            const id = this.tableLaneBuilder.selectedRow.data.get("id");
            if(reviewResults) {
                PnnLaneBuilderProfileList.showResultsSlideout(data, summary, id);
            }
            else if(reviewResults === false) { //do not post changes if reviewResults is undefined
                this.postChanges();
            }
        }
    }

    static async showResultsSlideout(data: ModelRow[], summary: ModelRow, profileId: String, history: boolean = false) {
        const layout = Layout.getLayout("lme/dispatch/pnn/LaneBuilderResultsSlideout") as LaneBuilderResultsSlideout;

        const saveButton = new Button({
            rowBreak: false,
            caption: "Update & Close",
            backgroundColor: "primary",
            borderWidth: 0,
            color: "primary.reverse",
            onClick: () => {
                saveButton.busy = true;
                layout.postChanges(profileId).then(() => {
                    layout.slideOut();
                }).finally(() => {
                    saveButton.busy = false
                });
            }
        });
        
        SlideoutDecorator.decorateAndSlideIn(layout, {
            addlComponents: !history ? [saveButton] : [],
            fillVerticalSpace: true, 
            width: 1200, 
            title: "PNN Builder Results",
            onClose: (cancelled: boolean) => {
                if (cancelled)
                    layout.slideOut();
            },
            doAfterSlideIn: () => {
                layout.labelOrderCount.text = summary.getNumber("number_of_orders", 0).toString();
                layout.labelCarrierCount.text = summary.getNumber("number_of_carriers", 0).toString();
                layout.tableCarriers.displayComponentData(null, data, 0);
                if (history) {
                    layout.panelResults.visible = false;
                    layout.tableCarriers.selectionMode = SelectionMode.NONE;
                }
                else {
                    layout.tableCarriers.selectRow(layout.tableCarriers.rows[0], SelectionType.SINGLE, null);
                }
            }
        });
    }

    async postChanges() {
        await Api.update("lme/powerbroker/pnn-lane-builder", { profile_id: this.tableLaneBuilder.selectedRow?.data?.get("id") })
    }

    /** This is an event handler for the onRowDisplay event of tableLaneBuilder. */
    tableLaneBuilderOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow()
        const startTb = tableRow.findComponentById("textboxStartDate") as Textbox
        const endTb = tableRow.findComponentById("textboxEndDate") as Textbox
        startTb.text = DateUtil.formatDate(DateUtil.parseDateWithKeywords(startTb.text, true))
        endTb.text = DateUtil.formatDate(DateUtil.parseDateWithKeywords(endTb.text, true))
    }
}
